/**
 * Linked cards and shopping rewards for the signed-in customer
 * (server/fidel-rewards.js routes). One hook for Profile's "Linked cards" and
 * Rewards' "Shop & earn", so both stay in step after a link or an unlink.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { openFidelCardForm } from '@/lib/fidelSdk';

async function authed(path, init = {}) {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error('Please sign in again.');
  const res = await fetch(path, { ...init, headers: { ...(init.headers || {}), Authorization: `Bearer ${token}`, ...(init.body ? { 'Content-Type': 'application/json' } : {}) } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || 'Something went wrong. Please try again.');
  return body;
}

export function useCardLinkedMiles({ withOffers = false } = {}) {
  const [state, setState] = useState({ loading: true, cards: [], pendingMiles: 0, earnedMiles: 0, activity: [], offers: [] });
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [shopping, offers] = await Promise.all([
        authed('/api/rewards/shopping'),
        withOffers ? authed('/api/rewards/offers').catch(() => ({ offers: [] })) : Promise.resolve({ offers: [] }),
      ]);
      setState({ loading: false, ...shopping, offers: offers.offers || [] });
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
      setError(e.message);
    }
  }, [withOffers]);

  useEffect(() => { refresh(); }, [refresh]);

  const linkCard = useCallback(async () => {
    setError(null);
    setLinking(true);
    try {
      const config = await authed('/api/rewards/cards/sdk-config');
      await openFidelCardForm(config, {
        onEnrolled: async (card) => {
          try {
            await authed('/api/rewards/cards/confirm', { method: 'POST', body: JSON.stringify({ cardId: card?.id }) });
            await refresh();
          } catch (e) {
            setError(e.message);
          } finally {
            setLinking(false);
          }
        },
        onFailed: (err) => {
          setLinking(false);
          setError(err?.type === 'item-exists' ? 'That card is already linked.' : 'We couldn’t link that card. Please check the details and try again.');
        },
        onCancelled: () => setLinking(false),
      });
    } catch (e) {
      setLinking(false);
      setError(e.message);
    }
  }, [refresh]);

  const unlinkCard = useCallback(async (cardId) => {
    setError(null);
    try {
      const out = await authed(`/api/rewards/cards/${encodeURIComponent(cardId)}`, { method: 'DELETE' });
      setState((s) => ({ ...s, cards: out.cards || [] }));
    } catch (e) {
      setError(e.message);
    }
  }, []);

  return { ...state, linking, error, linkCard, unlinkCard, refresh };
}
